import { useCallback } from 'react'
import type { Action } from '@/domains/shared/permissions/actions'
import { useAuthStore } from '@/store/useAuthStore'

// A stable empty list: a selector that returned a fresh `[]` on every call would
// make useSyncExternalStore re-render forever for a signed-out user.
const NO_ACTIONS: readonly string[] = []

function holdsAll(granted: readonly string[], required: Action | readonly Action[]): boolean {
  const list = Array.isArray(required) ? required : [required as Action]
  return list.every((action) => granted.includes(action))
}

/**
 * Whether the signed-in user holds every listed catalogue action (ADR 0020). Reads
 * the resolved `actions` the profile returns; a missing profile holds nothing.
 * This is the one way the web app asks "can this user do X": the route guard, the
 * sidebar and every hidden control go through it.
 */
export function useHasPermission(action: Action | readonly Action[]): boolean {
  return useAuthStore((s) => holdsAll(s.user?.actions ?? NO_ACTIONS, action))
}

/**
 * The same check as a predicate, for callers that decide over a list (the sidebar
 * filters its entries) and cannot call a hook per item.
 */
export function usePermissionCheck(): (action: Action | readonly Action[]) => boolean {
  const granted = useAuthStore((s) => s.user?.actions ?? NO_ACTIONS)
  return useCallback((action) => holdsAll(granted, action), [granted])
}

/**
 * Whether the signed-in user holds the SuperAdmin grant. The profile already
 * resolves every action for them, so this only gates what is not an action:
 * the Companies pages and the controls on granted users.
 */
export function useIsSuperAdmin(): boolean {
  return useAuthStore((s) => s.user?.superAdmin ?? false)
}
